/****************************************************
 * Module Name:  LockedPlanning.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState, useMemo, useEffect } from "react";
import { Box, Tooltip, Typography, Stack, Button, Switch, TextField, FormControlLabel, FormGroup, } from "@mui/material";
import Select from "react-select";
import Constants from "../../../utility/constants";
import NoPermission from "../../common/noPermission";
import Utility from "../../../utility/utility";
import { LoadingButton } from "@mui/lab";
import PricePlanningProxy from "../../../utility/proxy/manager/manufacturingProxy/pricePlanningProxy";
import { DataGridPro, GridToolbar, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { BiSearchAlt2 } from "react-icons/bi";
import TabMenuSnackbar from "../../common/tabMenuSnackbar";
import renderCellExpand from "../../common/dataGridCellExpand";

import PricePlanDetailModal from "./pricePlanDetailModal";
import KapanHistoryModal from "./kapanHistoryModal";
import PacketHistoryModal from "../../common/packetHistoryModal";
import PageTitle from "../../common/pageTitle";
import PacketProxy from "../../../utility/proxy/manager/manufacturingProxy/packetProxy";

const LockedPlanning = () => {

    const [kapanId, setKapanId] = useState("");
    const [packetId, setPacketId] = useState("");
    const [lockedPlanningLoader, setLockedPlanningLoader] = useState(false);
    const [rows, setRows] = useState([]);
    const [filterValues, setFilterValues] = useState({});

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");

    const [showPricePlanDetailModal, setShowPricePlanDetailModal] = useState(false);
    const [stoneId, setStoneId] = useState("");
    const [selectedRow, setSelectedRow] = useState({});

    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);
    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);

    const [kapanPacketOptions, setKapanPacketOptions] = useState([]);
    const [kapanOptions, setKapanOptions] = useState([]);
    const [packetOptions, setPacketOptions] = useState([]);
    const [selectedKapan, setSelectedKapan] = useState(null);
    const [selectedPacket, setSelectedPacket] = useState(null);
    const [issued, setIssued] = useState(false);

    const [lockedPricePlanningPermission] = React.useState(
        Constants.USER_PERMISSION.filter(
            (iPermission) =>
                iPermission.permissionid === Constants.PERMISSIONS.MANUFACTURING_OPERATION_LOCKEDPLANNING ||
                iPermission.permissionid === Constants.PERMISSIONS.MANUFACTURING_OPERATION_LOCKEDPLANNING_MARKER
        )
    );

    useEffect(() => {
        PageTitle("MANUFACTURING_OPERATION_PRICEPLANNING");
    }, []);

    useEffect(() => {
        setKapanId("");
        setPacketId("");
        setSelectedKapan(null);
        setSelectedPacket(null);
        if (issued) {
            getIssuedKapanPackets();
        }
    }, [issued]);

    const isChecker = React.useMemo(() => {

        return lockedPricePlanningPermission &&
            lockedPricePlanningPermission[0].permissionid === Constants.PERMISSIONS.MANUFACTURING_OPERATION_LOCKEDPLANNING_MARKER
            ? false
            : lockedPricePlanningPermission &&
                lockedPricePlanningPermission[0].permissionid === Constants.PERMISSIONS.MANUFACTURING_OPERATION_LOCKEDPLANNING
                ? true
                : null;

    }, [lockedPricePlanningPermission]);

    const handleSearch = () => {
        if (issued && kapanId === "") {
            return;
        }
        setLockedPlanningLoader(true);
        let data = {
            userid: Constants.loggedInUser.id,
            kapanid: parseInt(kapanId),
            packetid: packetId,
            status: 'LOCKED',
            date: null,
            ischecker: isChecker,
        };
        PricePlanningProxy.getAllPricePlanningByFilters(data, (responseData) => {
            if (responseData) {
                const transformedData = responseData.map(item => ({
                    ...item,
                    priceplanningdetail: item.priceplanningdetail.map(detail => ({
                        ...detail,
                        stonetype: detail.stonetype === "L" ? "Lab Grown Diamonds" : "Natural Diamonds",
                    })),
                }));

                // Flatten and process the details for filter values
                const response = transformedData.flatMap(item => item.priceplanningdetail);
                const uniqueFilterValues = {
                    createdbyusername: Utility.getDistinctOptions(response, "createdbyusername"),
                    shape: Utility.getDistinctOptions(response, "shape"),
                    color: Utility.getDistinctOptions(response, "color"),
                    cut: Utility.getDistinctOptions(response, "cut"),
                    pol: Utility.getDistinctOptions(response, "pol"),
                    sym: Utility.getDistinctOptions(response, "sym"),
                    clarity: Utility.getDistinctOptions(response, "clarity"),
                    flo: Utility.getDistinctOptions(response, "flo"),
                    stonetype: Utility.getDistinctOptions(response, "stonetype"),
                    lockedbyusername: Utility.getDistinctOptions(response, "lockedbyusername"),
                    status: Utility.getDistinctOptions(response, "status"),
                    lab: Utility.getDistinctOptions(response, "lab"),
                };

                setFilterValues(uniqueFilterValues);
                setLockedPlanningLoader(false);
                setRows(transformedData);
            } else {
                setLockedPlanningLoader(false);
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
            }
        });
    }

    const handleKapanChange = (selected) => {
        setSelectedKapan(selected);

        const kapanVal = selected ? selected.value : "";
        setKapanId(kapanVal);

        if (!selected) {
            // Reset packet options to all unique packets
            const uniquePacketsMap = new Map();
            kapanPacketOptions.forEach(pair => {
                if (!uniquePacketsMap.has(pair.packetid)) {
                    uniquePacketsMap.set(pair.packetid, {
                        label: pair.packetid,
                        value: pair.packetid
                    });
                }
            });
            const uniquePackets = Array.from(uniquePacketsMap.values());
            setPacketOptions(uniquePackets);
            return;
        }

        // Filter packet options that match the selected kapan
        const matchingPackets = kapanPacketOptions
            .filter(pair => pair.kapanid === selected.value)
            .map(pair => ({ label: pair.packetid, value: pair.packetid }));

        setPacketOptions(matchingPackets);

        // If any selected packets don't match the new kapan, remove them
        if (selectedPacket && selectedPacket.length > 0) {
            const validPackets = selectedPacket.filter(packet =>
                kapanPacketOptions.some(p =>
                    p.kapanid === selected.value && p.packetid === packet.value
                )
            );
            if (validPackets.length === 0) {
                setSelectedPacket(null);
                setPacketId("");
            }
        }
    };

    const handlePacketChange = (selected) => {
        setSelectedPacket(selected);

        const packetIds = selected ? selected.map(p => p.value) : [];
        const packetIdStr = packetIds.join(",");
        setPacketId(packetIdStr);

        // If no packet is selected, show all unique kapan options
        if (packetIds.length === 0) {
            const uniqueKapanMap = new Map();
            kapanPacketOptions.forEach(pair => {
                if (!uniqueKapanMap.has(pair.kapanid)) {
                    uniqueKapanMap.set(pair.kapanid, {
                        label: pair.kapanid,
                        value: pair.kapanid
                    });
                }
            });
            const uniqueKapans = Array.from(uniqueKapanMap.values());
            setKapanOptions(uniqueKapans);
            return;
        }

        // Find kapan IDs that are associated with ALL selected packets
        const kapanGroups = packetIds.map(pid =>
            new Set(
                kapanPacketOptions
                    .filter(p => p.packetid === pid)
                    .map(p => p.kapanid)
            )
        );

        // Get intersection of all kapan sets (i.e. all kapans that are associated with all selected packets)
        const intersection = kapanGroups.reduce((acc, set) => {
            return new Set([...acc].filter(x => set.has(x)));
        });

        const matchingKapans = Array.from(intersection).map(kapanid => ({
            label: kapanid,
            value: kapanid
        }));

        setKapanOptions(matchingKapans);

        // Validate selected kapan
        if (selectedKapan && !intersection.has(selectedKapan.value)) {
            setSelectedKapan(null);
            setKapanId("");
        }
    };


    const getIssuedKapanPackets = () => {
        PacketProxy.GetMyIssuedKapanPacket((response) => {
            if (response) {
                setKapanPacketOptions(response);
                const kapanSet = new Set();
                const packetSet = new Set();

                response.forEach(({ kapanid, packetid }) => {
                    if (kapanid && packetid) {
                        kapanSet.add(kapanid);
                        packetSet.add(packetid);
                    }
                });

                setKapanOptions([...kapanSet].map(id => ({ label: id, value: id })));
                setPacketOptions([...packetSet].map(id => ({ label: id, value: id })));
            }
            else {
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
            }
        });
    }

    const initialColumns = () => [
        {
            field: 'kapanid',
            headerName: 'KAPANID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <Button
                    sx={{ textDecorationLine: "underline" }}
                    onClick={() => {
                        setKapanId(params.row.kapanid);
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
            ),
            width: 80
        },
        {
            field: 'packetid',
            headerName: 'PACKETID',
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Button
                    sx={{ textDecorationLine: "underline" }}
                    onClick={() => {
                        setPacketId(params.value);
                        setKapanId(params.row.kapanid);
                        setShowPacketDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
            ),
            width: 100
        },
        {
            field: 'planid',
            headerName: 'PLAN ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'totalcount',
            headerName: 'COUNT',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'totalsize',
            headerName: 'TOTAL SIZE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'totalstoneprice',
            headerName: 'TOTAL STONE PRICE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'totalprice',
            headerName: 'TOTAL PRICE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },

    ];

    const middleColumns = useMemo(() => [
        {
            field: 'id',
            headerName: 'ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'kapanid',
            headerName: 'KAPANID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'packetid',
            headerName: 'PACKETID',
            headerAlign: 'center',
            align: 'center',
            width: 80,
            renderCell: renderCellExpand,
        },
        {
            field: 'shape',
            headerName: 'SHAPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.shape : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'size',
            headerName: 'SIZE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'color',
            headerName: 'COLOR',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.color : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 65
        },
        {
            field: 'clarity',
            headerName: 'CLARITY',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.clarity : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 75
        },
        {
            field: 'cut',
            headerName: 'CUT',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.cut : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'pol',
            headerName: 'POL',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.pol : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'sym',
            headerName: 'SYM',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.sym : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'flo',
            headerName: 'FLO',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.flo : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 60
        },
        {
            field: 'lab',
            headerName: 'LAB',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.lab : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 60
        },
        {
            field: 'stonetype',
            headerName: 'STONE TYPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueGetter: (params) => params.value,
            valueOptions: filterValues ? filterValues.stonetype : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value,
            width: 150
        },
        {
            field: 'rapnetprice',
            headerName: 'RAP',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'ppc',
            headerName: 'PPC',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 80
        },
        {
            field: 'discount',
            headerName: 'DIS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            width: 80,
            editable: true,
            valueFormatter: (params) => params.value,
        },
        {
            field: 'labprice',
            headerName: 'LAB PRICE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'total',
            headerName: 'TOTAL',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 80
        },
        {
            field: 'stoneprice',
            headerName: 'STONE PRICE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 120
        },
        {
            field: 'view',
            headerName: 'VIEW',
            headerAlign: 'center',
            align: 'center',
            width: 100,
            renderCell: (params) => (
                <button
                    style={{
                        padding: '5px 10px',
                        cursor: 'pointer',
                        backgroundColor: '#000000',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '5px',
                    }}
                    onClick={() => handleViewButtonClick(params.row)}>
                    VIEW
                </button>
            ),
        },
        {
            field: 'iscertified',
            headerName: 'IS CERTIFIED',
            headerAlign: 'center',
            align: 'center',
            type: "boolean",
            width: 100
        },
        {
            field: 'createdby',
            headerName: 'CREATED BY ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'createdbyusername',
            headerName: 'CREATED BY',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.createdbyusername : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 100
        },
        {
            field: 'lockedby',
            headerName: 'LOCKED BY ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 100
        },
        {
            field: 'lockedbyusername',
            headerName: 'LOCKED BY',
            headerAlign: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.lockedbyusername : [],
            filterOperators: Utility.combinedFilters(),
            align: 'center',
            width: 100
        },
        {
            field: 'createdat',
            headerName: 'CREATED AT',
            headerAlign: 'center',
            align: 'center',
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            },
            width: 150
        },
        {
            field: 'updatedat',
            headerName: 'UPDATED AT',
            headerAlign: 'center',
            align: 'center',
            width: 110,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            }
        },
        {
            field: 'status',
            headerName: 'STATUS',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.status : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 100
        },
        {
            field: 'rapnetdate',
            headerName: 'RAPNET DATE',
            headerAlign: 'center',
            align: 'center',
            width: 100,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            }
        },
    ], [filterValues]);

    const handleViewButtonClick = (selectedRow) => {
        setSelectedRow(selectedRow)
        setStoneId(selectedRow.id);
        setShowPricePlanDetailModal(true);
    }

    const columns = initialColumns();

    const getDetailPanelHeight = React.useCallback(() => 'auto', []);

    const getDetailPanelContent = React.useCallback((row) => <DetailPanelContent row={row} />,
        [middleColumns],);

    function DetailPanelContent({ row: rowProp }) {

        let rows = rowProp.priceplanningdetail;

        return (
            <Stack
                sx={{ py: 2, height: '100%', boxSizing: 'border-box' }}
                direction="column"
            >
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "90vw",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        height: 'auto',
                        marginLeft: '16px',
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={true}
                        density="compact"
                        columns={middleColumns}
                        rows={rows}
                        getRowId={(row) => row.id}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "8px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "15px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "22px",
                            },

                            fontSize: Constants.defaultTableFontSize, fontWeight: 'bold',
                            maxHeight: '30vh',
                        }}
                        // loading={isLoading}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    id: false,
                                    createdby: false,
                                    lockedby: false,
                                    status: false,
                                    rapnetdate: false,
                                    createdat: false,
                                    kapanid: false,
                                    packetid: false,
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                    />
                </Box>
            </Stack>
        );
    }

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ flexGrow: 1, display: "block" }} />
                {lockedPlanningLoader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            onClick={() =>
                                handleSearch()
                            }
                            color="primary"
                            fontSize="medium"
                            sx={{ cursor: "pointer" }}
                        />
                    </Tooltip>
                )}
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        )
    }

    return (
        <Box>
            {
                !Utility.isReadAllowed(lockedPricePlanningPermission) ? <NoPermission /> : (
                    <Box px={1}>
                        <TabMenuSnackbar
                            show={showSnackbar}
                            message={snackbarMessage}
                            variant={snackbarVariant}
                            autoHide={Constants.SnackBarCloseTimer}
                            title={snackbarVariant === "success" ? "Success" :
                                (snackbarVariant === "error" ? "Failed" : "Warning")}
                            handleClose={() => setShowSnackbar(false)}
                        />
                        {showKapanDetailModal ? (
                            <KapanHistoryModal
                                show={showKapanDetailModal}
                                kapanId={kapanId}
                                close={() => setShowKapanDetailModal(false)}
                            />
                        ) : null}

                        {showPacketDetailModal ? (
                            <PacketHistoryModal
                                show={showPacketDetailModal}
                                packetId={packetId}
                                kapanId={kapanId}
                                close={() => setShowPacketDetailModal(false)}
                            />
                        ) : null}
                        {showPricePlanDetailModal ? (
                            <PricePlanDetailModal
                                show={showPricePlanDetailModal}
                                stoneId={stoneId}
                                kapanid={selectedRow.kapanid}
                                packetid={selectedRow.packetid}
                                handleClose={() => setShowPricePlanDetailModal(false)}
                            />
                        ) : null}
                        <>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "30px",
                                    width: "100%",
                                    paddingTop: "13px",
                                    "& .MuiOutlinedInput-input": {
                                        padding: "9px !important",
                                        height: "25px !important"
                                    },
                                    "& .MuiInputLabel-root": {
                                        top: "-7px !important"
                                    },
                                    "& .MuiOutlinedInput-root": {
                                        padding: 0
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 4,
                                        alignItems: "center",
                                        flex: 1,
                                        minWidth: 0
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            flex: "1 1 280px",
                                            maxWidth: 350,
                                            minWidth: 240
                                        }}
                                    >
                                        <Typography sx={{ minWidth: 80 }}>Kapan ID</Typography>
                                        {issued ? (
                                            <Select
                                                options={kapanOptions}
                                                value={selectedKapan}
                                                onChange={handleKapanChange}
                                                placeholder="Select Kapan"
                                                isClearable={true}
                                                styles={{
                                                    container: (base) => ({ ...base, flex: 1 }),
                                                    control: (base) => ({ ...base, minHeight: 38 })
                                                }}
                                            />
                                        ) : (
                                            <TextField
                                                sx={{ width: "80%", margin: 0 }}
                                                margin="normal"
                                                label="Kapan Id"
                                                placeholder="Enter Kapan Id"
                                                value={kapanId}
                                                onChange={(e) => {
                                                    if (/^[1-9]\d*$/.test(e.target.value) || e.target.value === "") {
                                                        setKapanId(e.target.value);
                                                    }
                                                }}
                                                styles={{
                                                    container: (base) => ({ ...base, flex: 1 }),
                                                    control: (base) => ({ ...base, minHeight: 38 }),
                                                }}
                                            />
                                        )}
                                    </Box>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            flex: "1 1 280px",
                                            maxWidth: 350,
                                            minWidth: 240
                                        }}
                                    >
                                        <Typography sx={{ minWidth: 80 }}>Packet ID</Typography>
                                        {issued ? (
                                            <Select
                                                isMulti
                                                options={packetOptions}
                                                value={selectedPacket}
                                                onChange={handlePacketChange}
                                                placeholder="Select Packet"
                                                isClearable={true}
                                                styles={{
                                                    container: (base) => ({ ...base, flex: 1 }),
                                                    control: (base) => ({ ...base, minHeight: 38 })
                                                }}
                                            />
                                        ) : (
                                            <TextField
                                                sx={{ width: "80%", margin: 0 }}
                                                margin="normal"
                                                label="Packet Id"
                                                placeholder="Enter Packet Id"
                                                multiline
                                                value={packetId}
                                                onChange={(e) => setPacketId(Utility.ConvertNewLineToComma(e.target.value))}
                                                styles={{
                                                    container: (base) => ({ ...base, flex: 1 }),
                                                    control: (base) => ({ ...base, minHeight: 38 }),
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            flex: "0 1 auto",
                                            minWidth: 40,
                                        }}
                                    >
                                        <FormGroup>
                                            <FormControlLabel
                                                control={
                                                    <Box>
                                                        <Switch
                                                            checked={issued}
                                                            onChange={() => setIssued(!issued)}
                                                        />
                                                        <Typography>Issued</Typography>
                                                    </Box>
                                                }
                                                label=""
                                            />
                                        </FormGroup>
                                    </Box>
                                    <Box
                                        sx={{
                                            "& .Mui-disabled": {
                                                backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                                color: "rgba(0, 0, 0, 0.26) !important"
                                            },
                                            alignSelf: "center"
                                        }}
                                    >
                                        <LoadingButton
                                            sx={{ mr: 1 }}
                                            variant="contained"
                                            onClick={handleSearch}
                                            loading={lockedPlanningLoader}
                                            loadingPosition="start"
                                            startIcon={<BiSearchAlt2 />}
                                            disabled={issued && kapanId === ""}
                                        >
                                            Search
                                        </LoadingButton>
                                    </Box>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    width: "100%",
                                    backgroundColor: '#fff',
                                    color: '#000',
                                    position: "relative",
                                    paddingTop: "20px",
                                    "& .MuiTablePagination-selectLabel": {
                                        marginTop: "15px",
                                    },
                                    "& .MuiTablePagination-displayedRows": {
                                        marginTop: "15px",
                                    },
                                    "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                        fontWeight: Constants.defaultFontWeight,
                                    },
                                    "& .css-cc8tf1": {
                                        fontWeight: Constants.defaultFontWeight,
                                    },
                                    height: "71vh",
                                }}
                            >
                                <DataGridPro
                                    disableRowSelectionOnClick={true}
                                    sx={{
                                        '& .MuiDataGrid-columnHeader': {
                                            color: '#fff',
                                            backgroundColor: 'grey',
                                        },
                                        "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                            py: "4px",
                                        },
                                        "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                            py: "12px",
                                        },
                                        "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                            py: "18px",
                                        },

                                        fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                    }}
                                    density="compact"
                                    loading={lockedPlanningLoader}
                                    slots={{ toolbar: CustomToolbar }}
                                    rows={rows}
                                    columns={columns}
                                    getRowId={(row) => row.embededkey}
                                    initialState={{
                                        columns: {
                                            columnVisibilityModel: {
                                                // Hide columns id, the other columns will remain visible
                                                id: false,
                                                createdby: false,
                                                createdbyusername: false,
                                                lockedby: false,
                                                status: false,
                                                rapnetdate: false,
                                                createdat: false
                                            },
                                        },
                                        pagination: {
                                            paginationModel: Constants.pageSize,
                                        },
                                        sorting: {
                                            sortModel: [{ field: "updatedat", sort: "desc" }],
                                        },
                                    }}
                                    pageSizeOptions={Constants.pageSizeOptions}
                                    pagination
                                    slotProps={{
                                        toolbar: {
                                            showQuickFilter: true,
                                            quickFilterProps: { debounceMs: 500 },
                                        },
                                    }}
                                    getDetailPanelHeight={getDetailPanelHeight}
                                    getDetailPanelContent={({ row }) => getDetailPanelContent(row)}
                                />
                            </Box></>
                    </Box>
                )}
        </Box>
    );
};

export default LockedPlanning;